import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, ClipboardCheck, BarChart3, FolderOpen, LineChart, ArrowUpRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const cards = [
  { to: "/generate", icon: Sparkles, title: "Generate Practice Paper", desc: "Create an original, exam-realistic paper in minutes.", grad: "from-indigo-500 to-blue-500" },
  { to: "/predictions", icon: TrendingUp, title: "AI Predictions", desc: "See which topics are most likely to come up next.", grad: "from-purple-500 to-indigo-500" },
  { to: "/examiner", icon: ClipboardCheck, title: "Mark My Paper", desc: "Get examiner-quality marking and an estimated grade.", grad: "from-blue-500 to-cyan-500" },
  { to: "/analytics", icon: BarChart3, title: "My Progress", desc: "Track scores, weakest topics and revision time.", grad: "from-fuchsia-500 to-purple-500" },
  { to: "/papers", icon: FolderOpen, title: "Saved Papers", desc: "Revisit and download every paper you've generated.", grad: "from-indigo-500 to-purple-500" },
  { to: "/insights", icon: LineChart, title: "Analytics", desc: "Explore topic frequencies and trends across past exams.", grad: "from-cyan-500 to-blue-500" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/analytics/me").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="dashboard-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Dashboard</p>
      <h1 className="text-4xl md:text-5xl font-bold tracking-tighter">Hey, {user?.name?.split(" ")[0]} 👋</h1>
      <p className="mt-3 text-muted-foreground">What shall we revise today?</p>

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stats">
          {[
            { label: "Papers generated", value: stats.papers_generated },
            { label: "Papers marked", value: stats.papers_completed },
            { label: "Average score", value: `${stats.average_score}%` },
            { label: "Minutes revised", value: stats.time_spent_minutes },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-border bg-card px-5 py-4">
              <p className="text-2xl font-bold font-display">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((c, i) => (
          <motion.div key={c.to + c.title} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Link to={c.to} data-testid={`dashboard-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group block card-lift rounded-3xl border border-border bg-card p-7 h-full">
              <div className="flex items-start justify-between">
                <div className={`h-11 w-11 rounded-2xl bg-gradient-to-br ${c.grad} flex items-center justify-center`}>
                  <c.icon className="h-5 w-5 text-white" />
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <h3 className="mt-5 text-lg font-semibold tracking-tight">{c.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
